import React, { useEffect, useMemo, useRef, useState } from "react";
import { Globe } from "lucide-react";
import { EntireObjectIcon } from "../../ui/Icons";
import {
  SCENE_GRAPH_NODE_COLORS as NODE_COLORS,
  SCENE_GRAPH_NODE_RADIUS as NODE_RADIUS,
} from "../../../utils/sceneGraphNodeAppearance";
import {
  ROOT_OBJECT_NODE_Y_OFFSET,
  SCENE_GRAPH_EDITOR_EDGE_HIT_STROKE,
  SCENE_GRAPH_PARALLEL_ARC_BOW_STEP,
  FIXED_JOINT_GROUND_STEM_PX,
  FIXED_JOINT_GROUND_HALF_WIDTH_PX,
  OBJECT_MOVEMENT_TOOLTIP_LABEL,
  SCENE_GRAPH_TENDON_ACTUATED_DASH_PX,
  SCENE_GRAPH_TENDON_ACTUATED_GAP_PX,
  SELECTABLE_TYPES,
  EDITOR_OPEN_TYPES,
} from "../constants";
import { estimateLabelWidth } from "../helpers";
import { isSceneGraphSiteNode, isSceneGraphTypeHiddenInView } from "../gestures";
import { getSceneGraphEdgeProps } from "../edgeAppearance";
import { SceneNodeIcon } from "./icons";

const getEdgeProps = getSceneGraphEdgeProps;
const LAYOUT_ANIMATION_MS = 800;
const EMPTY_ROOT_BODY_LABELS = [];
const REPARENT_WORLD_TARGET = "__world__";
const layoutEaseOut = (t) => 1 - Math.pow(1 - t, 3);

const getNodeAnimationKey = (node) => `${node.type}-${node.uuid ?? node.id}`;
const getRootLabelAnimationKey = (label, index) =>
  `root-label-${label.rootBodyUuid ?? label.name ?? index}`;

const buildLayoutPositions = (nodes, rootBodyLabels = []) => {
  const nodePositions = new Map();
  const rootLabelPositions = new Map();
  nodes.forEach((node) => {
    nodePositions.set(getNodeAnimationKey(node), { x: node.x, y: node.y });
  });
  rootBodyLabels.forEach((label, index) => {
    rootLabelPositions.set(getRootLabelAnimationKey(label, index), { x: label.x, y: label.y });
  });
  return { nodePositions, rootLabelPositions };
};

const layoutPositionSignature = (nodes, rootBodyLabels = []) =>
  JSON.stringify({
    nodes: nodes.map((node) => [getNodeAnimationKey(node), node.x, node.y]),
    rootLabels: rootBodyLabels.map((label, index) => [
      getRootLabelAnimationKey(label, index),
      label.x,
      label.y,
    ]),
  });

const hasPositionChanges = (from, to) => {
  for (const [key, target] of to.nodePositions) {
    const source = from.nodePositions.get(key);
    if (source && (source.x !== target.x || source.y !== target.y)) return true;
  }
  for (const [key, target] of to.rootLabelPositions) {
    const source = from.rootLabelPositions.get(key);
    if (source && (source.x !== target.x || source.y !== target.y)) return true;
  }
  return false;
};

const interpolatePositionMap = (fromMap, toMap, progress) => {
  const next = new Map();
  toMap.forEach((target, key) => {
    const source = fromMap.get(key) || target;
    next.set(key, {
      x: source.x + (target.x - source.x) * progress,
      y: source.y + (target.y - source.y) * progress,
    });
  });
  return next;
};

const interpolateLayoutPositions = (from, to, progress) => ({
  nodePositions: interpolatePositionMap(from.nodePositions, to.nodePositions, progress),
  rootLabelPositions: interpolatePositionMap(
    from.rootLabelPositions,
    to.rootLabelPositions,
    progress
  ),
});

export default function SceneGraphSvgContent({
  diagramData,
  contentPanGroupRef,
  contentPanGroupTransform,
  effectiveHiddenTypes,
  reparentPick,
  cancelReparentDrag,
  reparentDragHoverBodyUuid,
  reparentDragGhost,
  reparentPickMoveSet,
  reparentPickInvalidParentUuids,
  selectedBody,
  lastSelectionSource,
  openObjectPropertiesFromSceneGraphRoot,
  showSceneGraphTooltip,
  hideSceneGraphTooltip,
  onSceneGraphEditorCurvePointerDown,
  onSceneGraphEditorCurveClick,
  openContextMenuForObject,
  selectBodyForSceneGraphTarget,
  getNodeFill,
  getPrimitiveVisualGeomKey,
  openContextMenuForBody,
  openContextMenuForJoint,
  openContextMenuForGeom,
  openContextMenuForSite,
  openContextMenuForCamera,
  openContextMenuForInertial,
  handleNodeClick,
  handleReparentDragPointerDown,
}) {
  const { nodes, edges } = diagramData;
  const rootBodyLabels = diagramData.rootBodyLabels || EMPTY_ROOT_BODY_LABELS;
  const prevNodeKeysRef = useRef(null);
  const prevEdgeKeysRef = useRef(null);
  const animationFrameRef = useRef(0);
  const latestLayoutPositionsRef = useRef(null);
  const currentAnimatedPositionsRef = useRef(null);
  const [animatedLayoutPositions, setAnimatedLayoutPositions] = useState(null);
  const targetPositionSignature = useMemo(
    () => layoutPositionSignature(nodes, rootBodyLabels),
    [nodes, rootBodyLabels]
  );
  const targetLayoutPositions = useMemo(
    () => buildLayoutPositions(nodes, rootBodyLabels),
    [nodes, rootBodyLabels]
  );
  useEffect(() => {
    const previous =
      currentAnimatedPositionsRef.current || latestLayoutPositionsRef.current || null;
    if (!previous) {
      latestLayoutPositionsRef.current = targetLayoutPositions;
      currentAnimatedPositionsRef.current = null;
      setAnimatedLayoutPositions(null);
      return;
    }

    if (!hasPositionChanges(previous, targetLayoutPositions)) {
      latestLayoutPositionsRef.current = targetLayoutPositions;
      currentAnimatedPositionsRef.current = null;
      setAnimatedLayoutPositions(null);
      return;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = 0;
    }

    const startedAt = performance.now();
    const animate = (now) => {
      const elapsed = now - startedAt;
      const progress = Math.min(1, elapsed / LAYOUT_ANIMATION_MS);
      const eased = layoutEaseOut(progress);
      const next = interpolateLayoutPositions(previous, targetLayoutPositions, eased);
      currentAnimatedPositionsRef.current = next;
      setAnimatedLayoutPositions(next);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      animationFrameRef.current = 0;
      latestLayoutPositionsRef.current = targetLayoutPositions;
      currentAnimatedPositionsRef.current = null;
      setAnimatedLayoutPositions(null);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = 0;
      }
      if (currentAnimatedPositionsRef.current) {
        latestLayoutPositionsRef.current = currentAnimatedPositionsRef.current;
      }
    };
  }, [targetPositionSignature, targetLayoutPositions]);

  const renderNodes = useMemo(() => {
    if (!animatedLayoutPositions) return nodes;
    return nodes.map((node) => {
      const position = animatedLayoutPositions.nodePositions.get(getNodeAnimationKey(node));
      return position ? { ...node, x: position.x, y: position.y } : node;
    });
  }, [animatedLayoutPositions, nodes]);
  const renderRootBodyLabels = useMemo(() => {
    if (!animatedLayoutPositions) return rootBodyLabels;
    return rootBodyLabels.map((label, index) => {
      const position = animatedLayoutPositions.rootLabelPositions.get(
        getRootLabelAnimationKey(label, index)
      );
      return position ? { ...label, x: position.x, y: position.y } : label;
    });
  }, [animatedLayoutPositions, rootBodyLabels]);
  const renderNodeById = useMemo(
    () => new Map(renderNodes.map((node) => [node.id, node])),
    [renderNodes]
  );
  const reparentEdgeDim = reparentPick ? 0.3 : 1;
  const moveToWorldAnchor = useMemo(() => {
    if (reparentPick?.kind !== "body") return null;
    const rootLabels = renderRootBodyLabels;
    if (rootLabels.length > 0) {
      const centerX = rootLabels.reduce((sum, label) => sum + label.x, 0) / rootLabels.length;
      const topLabelCenterY =
        Math.min(...rootLabels.map((label) => label.y)) - ROOT_OBJECT_NODE_Y_OFFSET;
      const topY = topLabelCenterY - 68;
      return { x: centerX, y: topY };
    }
    const bodyNodes = renderNodes.filter((node) => node.type === "body");
    if (bodyNodes.length === 0) return null;
    const centerX = bodyNodes.reduce((sum, node) => sum + node.x, 0) / bodyNodes.length;
    const topY = Math.min(...bodyNodes.map((node) => node.y)) - (NODE_RADIUS.body ?? 16) - 72;
    return { x: centerX, y: topY };
  }, [renderRootBodyLabels, renderNodes, reparentPick]);

  useEffect(() => {
    prevNodeKeysRef.current = new Set(
      diagramData.nodes.map((node) => node.uuid ?? `${node.type}-${node.id}`)
    );
    prevEdgeKeysRef.current = new Set(
      diagramData.edges.map((edge) => {
        const from = renderNodeById.get(edge.from);
        const to = renderNodeById.get(edge.to);
        if (edge.edgeType === "tendon-joint" && edge.tendonUuid) {
          return `tendon-joint-${edge.tendonUuid}-${from?.uuid ?? edge.from}-${to?.uuid ?? edge.to}`;
        }
        if (edge.edgeType === "equality-ref" && edge.equalityUuid) {
          return `equality-ref-${edge.equalityUuid}-${from?.uuid ?? edge.from}-${to?.uuid ?? edge.to}`;
        }
        if (edge.edgeType === "fixed-ground") {
          return `fixed-ground-${edge.jointUuid ?? edge.from}`;
        }
        return `${edge.edgeType}-${from?.uuid ?? edge.from}-${to?.uuid ?? edge.to}`;
      })
    );
  });

  return (
    <>
      <defs>
        <filter id="sceneGraphReparentGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feDropShadow dx="0" dy="0" stdDeviation="3.5" floodColor="#60a5fa" floodOpacity="0.92" />
        </filter>
      </defs>
      <g
        ref={contentPanGroupRef}
        transform={contentPanGroupTransform}
        data-scene-graph-content-pan-group="true"
      >
        {/* ── edges ──────────────────────────────────────────────────── */}
        {edges.map((edge) => {
          const from = renderNodeById.get(edge.from);
          if (!from) return null;
          if (edge.edgeType === "fixed-ground") {
            if (isSceneGraphTypeHiddenInView(from.type, effectiveHiddenTypes)) return null;
            const ep = getEdgeProps(edge);
            const stableKey = `fixed-ground-${edge.jointUuid ?? edge.from}`;
            const rootBodyUuid = edge.rootBodyUuid ?? from.uuid;
            const EASE = "0.18s ease-out";
            const isNewEdge =
              prevEdgeKeysRef.current !== null && !prevEdgeKeysRef.current.has(stableKey);
            const FADE = isNewEdge ? "sceneNodeFadeIn 0.12s ease-out both" : undefined;
            const groundX = edge.groundX ?? from.x;
            const groundY = edge.groundY ?? from.y - FIXED_JOINT_GROUND_STEM_PX;
            const bodyTopY = from.y - (NODE_RADIUS.body ?? 16);
            const supportHalfW = FIXED_JOINT_GROUND_HALF_WIDTH_PX;
            const hatchTopY = groundY - 1;
            const hatchBottomY = groundY - 12;

            return (
              <g
                key={stableKey}
                opacity={ep.opacity * reparentEdgeDim}
                data-scene-graph-no-pan="true"
                pointerEvents={reparentPick ? "none" : "auto"}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openObjectPropertiesFromSceneGraphRoot(rootBodyUuid);
                }}
                onMouseEnter={(event) =>
                  showSceneGraphTooltip(event, OBJECT_MOVEMENT_TOOLTIP_LABEL)
                }
                onMouseMove={(event) => showSceneGraphTooltip(event, OBJECT_MOVEMENT_TOOLTIP_LABEL)}
                onMouseLeave={hideSceneGraphTooltip}
                onFocus={(event) => showSceneGraphTooltip(event, OBJECT_MOVEMENT_TOOLTIP_LABEL)}
                onBlur={hideSceneGraphTooltip}
                aria-label={OBJECT_MOVEMENT_TOOLTIP_LABEL}
                tabIndex={reparentPick ? undefined : 0}
                style={{
                  cursor: reparentPick ? "default" : "pointer",
                  transition: `opacity ${EASE}`,
                  animation: FADE,
                }}
              >
                <path
                  d={`M ${from.x} ${bodyTopY} L ${groundX} ${groundY}`}
                  stroke={ep.stroke}
                  strokeWidth={ep.strokeWidth}
                  strokeLinecap="round"
                  fill="none"
                />
                <path
                  d={`M ${groundX - supportHalfW} ${groundY} L ${
                    groundX + supportHalfW
                  } ${groundY}`}
                  stroke={ep.stroke}
                  strokeWidth={ep.strokeWidth}
                  strokeLinecap="round"
                  fill="none"
                />
                {[-10, 0, 10].map((offset) => (
                  <path
                    key={offset}
                    d={`M ${groundX + offset - 5} ${hatchTopY} L ${
                      groundX + offset + 4
                    } ${hatchBottomY}`}
                    stroke={ep.stroke}
                    strokeWidth={1.4}
                    strokeLinecap="round"
                    fill="none"
                  />
                ))}
                <path
                  d={`M ${groundX - supportHalfW - 8} ${groundY - 14} L ${
                    groundX + supportHalfW + 8
                  } ${groundY + 2}`}
                  stroke="transparent"
                  strokeWidth={34}
                  strokeLinecap="round"
                  fill="none"
                />
              </g>
            );
          }
          const to = renderNodeById.get(edge.to);
          if (!to) return null;
          if (
            isSceneGraphTypeHiddenInView(from.type, effectiveHiddenTypes) ||
            isSceneGraphTypeHiddenInView(to.type, effectiveHiddenTypes)
          )
            return null;
          const ep = getEdgeProps(edge);
          // Stable key derived from node UUIDs so React keeps the same DOM
          // element across re-renders and CSS transitions animate correctly.
          const stableKey =
            edge.edgeType === "tendon-joint" && edge.tendonUuid
              ? `tendon-joint-${edge.tendonUuid}-${from.uuid ?? edge.from}-${to.uuid ?? edge.to}`
              : edge.edgeType === "equality-ref" && edge.equalityUuid
                ? `equality-ref-${edge.equalityUuid}-${from.uuid ?? edge.from}-${to.uuid ?? edge.to}`
                : `${edge.edgeType}-${from.uuid ?? edge.from}-${to.uuid ?? edge.to}`;
          const EASE = "0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
          const isNewEdge =
            prevEdgeKeysRef.current !== null && !prevEdgeKeysRef.current.has(stableKey);
          const FADE = isNewEdge ? "sceneNodeFadeIn 0.35s ease 0.45s both" : undefined;

          if (edge.edgeType === "body-parent-child" && edge.crossbarY != null) {
            const { crossbarY } = edge;
            const d = `M ${from.x} ${from.y} ` + `L ${from.x} ${crossbarY} ` + `L ${to.x} ${to.y}`;
            return (
              <path
                key={stableKey}
                d={d}
                stroke={ep.stroke}
                strokeWidth={ep.strokeWidth}
                strokeDasharray={ep.dasharray}
                opacity={ep.opacity * reparentEdgeDim}
                fill="none"
                style={{ transition: `d ${EASE}`, animation: FADE }}
              />
            );
          }

          if (edge.edgeType === "actuator-joint" || edge.edgeType === "actuator-tendon") {
            const jR = from.type === "tendon" ? NODE_RADIUS.tendon : NODE_RADIUS.joint;
            const sx = from.x + 4;
            const sy = from.y + jR - 1;
            const cpX = sx;
            const cpY = to.y;
            const curveD = `M ${sx} ${sy} Q ${cpX} ${cpY} ${to.x} ${to.y}`;
            const op = ep.opacity * reparentEdgeDim;

            if (edge.edgeType === "actuator-tendon") {
              const actD = SCENE_GRAPH_TENDON_ACTUATED_DASH_PX;
              const actG = SCENE_GRAPH_TENDON_ACTUATED_GAP_PX;
              const actDashPat = `${actD} ${actD + 2 * actG}`;
              const actYellowPhase = actD + actG;
              return (
                <g key={stableKey}>
                  <path
                    d={curveD}
                    stroke="#38bdf8"
                    strokeWidth={ep.strokeWidth}
                    strokeDasharray={actDashPat}
                    strokeLinecap="butt"
                    opacity={op}
                    fill="none"
                    style={{ transition: `d ${EASE}`, animation: FADE }}
                  />
                  <path
                    d={curveD}
                    stroke="#fbbf24"
                    strokeWidth={ep.strokeWidth}
                    strokeDasharray={actDashPat}
                    strokeDashoffset={actYellowPhase}
                    strokeLinecap="butt"
                    opacity={op}
                    fill="none"
                    style={{ transition: `d ${EASE}`, animation: FADE }}
                  />
                </g>
              );
            }

            return (
              <path
                key={stableKey}
                d={curveD}
                stroke={ep.stroke}
                strokeWidth={ep.strokeWidth}
                strokeDasharray={ep.dasharray}
                opacity={op}
                fill="none"
                style={{ transition: `d ${EASE}`, animation: FADE }}
              />
            );
          }

          if (edge.edgeType === "equality-ref" || edge.edgeType === "tendon-joint") {
            const dx = to.x - from.x;
            const dy = to.y - from.y;
            const len = Math.sqrt(dx * dx + dy * dy) || 1;
            const bowBase = edge.edgeType === "tendon-joint" ? 0.24 : 0.28;
            const bowMin = edge.edgeType === "tendon-joint" ? 18 : 22;
            const bowMax = edge.edgeType === "tendon-joint" ? 50 : 55;
            const baseBow = Math.min(bowMax, Math.max(bowMin, len * bowBase));
            const laneN = edge.parallelArcCount ?? 1;
            const laneI = edge.parallelArcLane ?? 0;
            const bowRaw =
              laneN <= 1
                ? baseBow
                : baseBow + (laneI - (laneN - 1) / 2) * SCENE_GRAPH_PARALLEL_ARC_BOW_STEP;
            const bow = Math.max(10, Math.min(130, bowRaw));
            const cpX = (from.x + to.x) / 2 + (dy / len) * bow;
            const cpY = (from.y + to.y) / 2 + (-dx / len) * bow;
            const curveD = `M ${from.x} ${from.y} Q ${cpX} ${cpY} ${to.x} ${to.y}`;
            const editorUuid =
              edge.edgeType === "tendon-joint" ? edge.tendonUuid : edge.equalityUuid;
            const editorType = edge.edgeType === "tendon-joint" ? "tendon" : "equality";
            const curveInteractive = !reparentPick && editorUuid != null && editorUuid !== "";
            const hitPointerEvents = curveInteractive ? "stroke" : "none";
            const tip =
              editorType === "tendon" ? "Click to edit tendon" : "Click to edit constraint";

            const dualStripeTendon = edge.edgeType === "tendon-joint" && edge.tendonHasActuator;
            const actD = SCENE_GRAPH_TENDON_ACTUATED_DASH_PX;
            const actG = SCENE_GRAPH_TENDON_ACTUATED_GAP_PX;
            const actDashPat = `${actD} ${actD + 2 * actG}`;
            const actYellowPhase = actD + actG;

            return (
              <g key={stableKey}>
                <title>{tip}</title>
                {dualStripeTendon ? (
                  <>
                    <path
                      d={curveD}
                      stroke="#38bdf8"
                      strokeWidth={ep.strokeWidth}
                      strokeDasharray={actDashPat}
                      strokeLinecap="butt"
                      opacity={ep.opacity * reparentEdgeDim}
                      fill="none"
                      pointerEvents="none"
                      style={{ transition: `d ${EASE}`, animation: FADE }}
                    />
                    <path
                      d={curveD}
                      stroke="#fbbf24"
                      strokeWidth={ep.strokeWidth}
                      strokeDasharray={actDashPat}
                      strokeDashoffset={actYellowPhase}
                      strokeLinecap="butt"
                      opacity={ep.opacity * reparentEdgeDim}
                      fill="none"
                      pointerEvents="none"
                      style={{ transition: `d ${EASE}`, animation: FADE }}
                    />
                  </>
                ) : (
                  <path
                    d={curveD}
                    stroke={ep.stroke}
                    strokeWidth={ep.strokeWidth}
                    strokeDasharray={ep.dasharray}
                    opacity={ep.opacity * reparentEdgeDim}
                    fill="none"
                    pointerEvents="none"
                    style={{ transition: `d ${EASE}`, animation: FADE }}
                  />
                )}
                <path
                  d={curveD}
                  stroke="transparent"
                  strokeWidth={SCENE_GRAPH_EDITOR_EDGE_HIT_STROKE}
                  strokeLinecap="round"
                  fill="none"
                  data-scene-graph-no-pan="true"
                  pointerEvents={hitPointerEvents}
                  style={{
                    cursor: curveInteractive ? "pointer" : undefined,
                    transition: `d ${EASE}`,
                  }}
                  onPointerDown={onSceneGraphEditorCurvePointerDown}
                  onClick={(e) => onSceneGraphEditorCurveClick(e, editorType, editorUuid)}
                />
              </g>
            );
          }

          return (
            <path
              key={stableKey}
              d={`M ${from.x} ${from.y} L ${to.x} ${to.y}`}
              stroke={ep.stroke}
              strokeWidth={ep.strokeWidth}
              strokeDasharray={ep.dasharray}
              opacity={ep.opacity * reparentEdgeDim}
              fill="none"
              style={{ transition: `d ${EASE}`, animation: FADE }}
            />
          );
        })}

        {/* ── root body labels ────────────────────────────────────────── */}
        {renderRootBodyLabels.map((label, i) => {
          const pillHeight = 34;
          const pillRadius = pillHeight / 2;
          const icoSize = 24;
          const labelX = 23;
          const oc = NODE_COLORS.tendon;
          const bodySel = NODE_COLORS.body;
          const labelWidth = pillRadius + labelX + estimateLabelWidth(label.name) + 14;
          const objectSelected =
            selectedBody === label.rootBodyUuid &&
            (lastSelectionSource === "sceneTreeObject" || lastSelectionSource === "objectLibrary");
          const labelBodyNode = { type: "body", uuid: label.rootBodyUuid };
          const labelInvalidReparentTarget =
            reparentPick && reparentPickInvalidParentUuids.has(label.rootBodyUuid);
          const labelMovingSource = reparentPick && reparentPickMoveSet.has(label.rootBodyUuid);
          const labelValidReparentTarget =
            reparentPick && !labelInvalidReparentTarget && !labelMovingSource;
          const labelDropHovered =
            labelValidReparentTarget && reparentDragHoverBodyUuid === label.rootBodyUuid;
          return (
            <g
              key={`root-label-${i}`}
              transform={`translate(${label.x}, ${label.y - ROOT_OBJECT_NODE_Y_OFFSET})`}
              data-scene-selectable-node="true"
              data-scene-reparent-target-body-uuid={
                labelValidReparentTarget ? label.rootBodyUuid : undefined
              }
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onPointerDown={(event) => {
                handleReparentDragPointerDown?.(labelBodyNode, event);
              }}
              onContextMenu={
                reparentPick
                  ? (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      cancelReparentDrag();
                    }
                  : (event) => openContextMenuForObject(event, label.rootBodyUuid)
              }
              onClick={
                reparentPick
                  ? (event) => {
                      event.stopPropagation();
                    }
                  : (event) => {
                      event.stopPropagation();
                      const targetUuid = label.rootBodyUuid;
                      if (!targetUuid) return;
                      selectBodyForSceneGraphTarget(targetUuid, "sceneTreeObject");
                      window.dispatchEvent(new CustomEvent("open-properties-panel"));
                    }
              }
              style={{
                transition: "transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                opacity: labelInvalidReparentTarget && !labelMovingSource ? 0.35 : 1,
                pointerEvents: "auto",
                cursor: reparentPick
                  ? labelInvalidReparentTarget
                    ? "not-allowed"
                    : "pointer"
                  : "pointer",
              }}
            >
              <rect
                x={-pillRadius}
                y={-pillRadius}
                width={labelWidth}
                height={pillHeight}
                rx={pillRadius}
                ry={pillRadius}
                fill={objectSelected ? bodySel.sel : oc.base}
                stroke={
                  labelDropHovered ? "#fbbf24" : objectSelected ? bodySel.selStroke : oc.stroke
                }
                strokeWidth={labelDropHovered ? 4 : objectSelected ? 3 : 2}
                filter={labelDropHovered ? "url(#sceneGraphReparentGlow)" : undefined}
                style={{ transition: "fill 0.25s ease, stroke 0.25s ease" }}
              />
              <foreignObject
                x={-icoSize / 2}
                y={-icoSize / 2}
                width={icoSize}
                height={icoSize}
                style={{ pointerEvents: "none" }}
              >
                <div className="flex h-full w-full items-center justify-center text-white">
                  <EntireObjectIcon className="h-5 w-5" aria-hidden="true" />
                </div>
              </foreignObject>
              <text
                x={labelX}
                y={0}
                dominantBaseline="middle"
                textAnchor="start"
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  fill: "#f3f4f6",
                  userSelect: "none",
                  pointerEvents: "none",
                }}
              >
                {label.name}
              </text>
            </g>
          );
        })}
        {reparentPick && moveToWorldAnchor
          ? (() => {
              const worldHovered = reparentDragHoverBodyUuid === REPARENT_WORLD_TARGET;
              return (
                <g
                  transform={`translate(${moveToWorldAnchor.x}, ${moveToWorldAnchor.y})`}
                  data-scene-graph-no-pan="true"
                  data-scene-reparent-world-target="true"
                  onPointerDown={(event) => {
                    event.stopPropagation();
                  }}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <circle
                    r={worldHovered ? 31 : 26}
                    fill={worldHovered ? "#f59e0b" : "#1d4ed8"}
                    stroke={worldHovered ? "#fde68a" : "#60a5fa"}
                    strokeWidth={worldHovered ? 5 : 3.5}
                    filter="url(#sceneGraphReparentGlow)"
                    style={{ transition: "fill 0.18s ease, stroke 0.18s ease, r 0.18s ease" }}
                  />
                  <foreignObject
                    x={-12}
                    y={-12}
                    width={24}
                    height={24}
                    style={{ pointerEvents: "none" }}
                  >
                    <Globe className="w-6 h-6 text-white" />
                  </foreignObject>
                  <text
                    x={40}
                    y={0}
                    dominantBaseline="middle"
                    textAnchor="start"
                    style={{
                      fontSize: "13px",
                      fill: worldHovered ? "#fde68a" : "#dbeafe",
                      stroke: "#0b0f14",
                      strokeWidth: 2,
                      strokeLinejoin: "round",
                      paintOrder: "stroke",
                      userSelect: "none",
                      pointerEvents: "none",
                    }}
                  >
                    Move to World
                  </text>
                </g>
              );
            })()
          : null}

        {/* ── nodes ──────────────────────────────────────────────────── */}
        {renderNodes.map((node) => {
          if (isSceneGraphTypeHiddenInView(node.type, effectiveHiddenTypes)) return null;
          const r = NODE_RADIUS[node.type] ?? 10;
          const { fill, stroke, strokeWidth } = getNodeFill(node);
          const selectable = SELECTABLE_TYPES.has(node.type);
          const opensEditor = EDITOR_OPEN_TYPES.has(node.type);
          const icoSize = r * 1.5;
          const bcBody = NODE_COLORS.body;

          const labelX = node.type === "body" ? r + 13 : r + 6;
          const labelAnchor = "start";
          const fontSize = node.type === "body" ? "12px" : "10px";
          const labelFill =
            node.type === "body" ? "#f3f4f6" : node.type === "joint" ? "#f3f4f6" : "#d1d5db";

          const siteValidReparentTarget =
            reparentPick &&
            isSceneGraphSiteNode(node) &&
            node.parentBodyUuid &&
            !reparentPickInvalidParentUuids.has(node.parentBodyUuid);
          const reparentTargetBodyUuid =
            reparentPick && node.type === "body"
              ? node.uuid
              : siteValidReparentTarget
                ? node.parentBodyUuid
                : null;
          const pickDropHovered =
            reparentTargetBodyUuid && reparentDragHoverBodyUuid === reparentTargetBodyUuid;
          const pickSatelliteDim = reparentPick && node.type !== "body" && !siteValidReparentTarget;
          const pickMovingSource =
            reparentPick && node.type === "body" && reparentPickMoveSet.has(node.uuid);
          const pickInvalidOtherBody =
            reparentPick &&
            node.type === "body" &&
            reparentPickInvalidParentUuids.has(node.uuid) &&
            !reparentPickMoveSet.has(node.uuid);
          const pickValidBodyParent =
            reparentPick && node.type === "body" && !reparentPickInvalidParentUuids.has(node.uuid);

          let circleR = r;
          let circleFill = fill;
          let circleStroke = stroke;
          let circleSw = strokeWidth;
          let circleFilter = null;
          let circleAnim = null;
          let gOpacity = pickSatelliteDim ? 0.25 : 1;
          let nodePointerEvents = pickSatelliteDim ? "none" : "auto";

          if (pickMovingSource) {
            circleFill = bcBody.sel;
            circleStroke = bcBody.selStroke;
            circleSw = 3;
            gOpacity = 1;
            nodePointerEvents = "auto";
          } else if (pickInvalidOtherBody) {
            gOpacity = 0.25;
          } else if (pickValidBodyParent) {
            circleR = r * 1.1;
            circleFill = bcBody.sel;
            circleStroke = bcBody.selStroke;
            circleSw = 4.5;
            circleFilter = "url(#sceneGraphReparentGlow)";
            circleAnim = "reparentValidPulse 1.2s ease-in-out infinite";
            gOpacity = 1;
            nodePointerEvents = "auto";
          } else if (siteValidReparentTarget) {
            circleR = r * 1.1;
            circleFill = bcBody.sel;
            circleStroke = bcBody.selStroke;
            circleSw = 4.5;
            circleFilter = "url(#sceneGraphReparentGlow)";
            circleAnim = "reparentValidPulse 1.2s ease-in-out infinite";
            gOpacity = 1;
            nodePointerEvents = "auto";
          }

          if (pickDropHovered) {
            circleR = r * 1.28;
            circleFill = "#f59e0b";
            circleStroke = "#fde68a";
            circleSw = 5;
            circleFilter = "url(#sceneGraphReparentGlow)";
            circleAnim = null;
            gOpacity = 1;
            nodePointerEvents = "auto";
          }

          let nodeCursor = "default";
          if (reparentPick && node.type === "body") {
            nodeCursor = pickValidBodyParent ? "pointer" : "not-allowed";
          } else if (reparentPick && isSceneGraphSiteNode(node)) {
            nodeCursor = siteValidReparentTarget ? "pointer" : "default";
          } else if (selectable || opensEditor) {
            nodeCursor = "pointer";
          }

          return (
            <g
              key={`${node.type}-${node.uuid ?? node.id}`}
              transform={`translate(${node.x}, ${node.y})`}
              data-scene-selectable-node={selectable || opensEditor ? "true" : undefined}
              data-scene-reparent-target-body-uuid={
                reparentPick &&
                reparentTargetBodyUuid &&
                !reparentPickInvalidParentUuids.has(reparentTargetBodyUuid) &&
                !reparentPickMoveSet.has(reparentTargetBodyUuid)
                  ? reparentTargetBodyUuid
                  : undefined
              }
              onContextMenu={
                reparentPick
                  ? (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      cancelReparentDrag();
                    }
                  : node.type === "body"
                    ? (event) => openContextMenuForBody(event, node.uuid)
                    : node.type === "joint"
                      ? (event) => openContextMenuForJoint(event, node.uuid)
                      : node.type === "mesh"
                        ? (event) => {
                            const geomKey =
                              node.parentBodyUuid && node.uuid
                                ? `${node.parentBodyUuid}:${node.uuid}`
                                : getPrimitiveVisualGeomKey(node);
                            if (geomKey) {
                              openContextMenuForGeom(event, geomKey);
                              return;
                            }
                          }
                        : node.type === "geom"
                          ? (event) =>
                              openContextMenuForGeom(
                                event,
                                node.parentBodyUuid && node.uuid
                                  ? `${node.parentBodyUuid}:${node.uuid}`
                                  : null
                              )
                          : isSceneGraphSiteNode(node)
                            ? (event) => openContextMenuForSite(event, node.uuid)
                            : node.type === "camera"
                              ? (event) => openContextMenuForCamera(event, node.uuid)
                              : node.type === "inertial"
                                ? (event) =>
                                    openContextMenuForInertial(
                                      event,
                                      node.parentBodyUuid && node.uuid
                                        ? `${node.parentBodyUuid}:${node.uuid}`
                                        : null
                                    )
                                : undefined
              }
              onClick={
                selectable || opensEditor
                  ? (event) => {
                      if (reparentPick) {
                        event.stopPropagation();
                        return;
                      }
                      handleNodeClick(node, event);
                    }
                  : undefined
              }
              onPointerDown={
                selectable || opensEditor
                  ? (event) => handleReparentDragPointerDown?.(node, event)
                  : undefined
              }
              style={{
                cursor: nodeCursor,
                opacity: gOpacity,
                pointerEvents: nodePointerEvents,
                transition:
                  "transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease",
                animation:
                  prevNodeKeysRef.current !== null &&
                  !prevNodeKeysRef.current.has(node.uuid ?? `${node.type}-${node.id}`)
                    ? "sceneNodeFadeIn 0.35s ease 0.45s both"
                    : undefined,
              }}
            >
              <circle
                r={circleR}
                fill={circleFill}
                stroke={circleStroke}
                strokeWidth={circleSw}
                filter={circleFilter || undefined}
                style={{
                  transition: "fill 0.25s ease, stroke 0.25s ease",
                  animation: circleAnim || undefined,
                }}
              />
              {pickMovingSource ? (
                <circle
                  r={r + 5}
                  fill="none"
                  stroke="#93c5fd"
                  strokeWidth={1.5}
                  strokeDasharray="5 4"
                  style={{ pointerEvents: "none", opacity: 0.88 }}
                />
              ) : null}
              {pickMovingSource ? (
                <text
                  x={labelX}
                  y={-r - 14}
                  dominantBaseline="middle"
                  textAnchor={labelAnchor}
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    fill: "#93c5fd",
                    stroke: "#0b0f14",
                    strokeWidth: 2,
                    strokeLinejoin: "round",
                    paintOrder: "stroke",
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                >
                  Moving
                </text>
              ) : null}
              <foreignObject
                x={-icoSize / 2}
                y={-icoSize / 2}
                width={icoSize}
                height={icoSize}
                style={{ pointerEvents: "none", opacity: pickSatelliteDim ? 0 : 1 }}
              >
                <SceneNodeIcon type={node.type} />
              </foreignObject>
              <text
                x={labelX}
                y={0}
                dominantBaseline="middle"
                textAnchor={labelAnchor}
                style={{
                  fontSize,
                  fill: labelFill,
                  stroke: "#0b0f14",
                  strokeWidth: node.type === "joint" ? 3 : 2,
                  strokeLinejoin: "round",
                  paintOrder: "stroke",
                  userSelect: "none",
                  pointerEvents: "none",
                  opacity: pickSatelliteDim ? 0.35 : 1,
                }}
              >
                {node.displayName ?? node.name}
              </text>
            </g>
          );
        })}
        {reparentDragGhost ? (
          <g
            transform={`translate(${reparentDragGhost.x}, ${reparentDragGhost.y})`}
            pointerEvents="none"
            opacity={0.56}
            style={{ filter: "drop-shadow(0 8px 14px rgba(0,0,0,0.35))" }}
          >
            {(() => {
              const ghostType = reparentDragGhost.type || "body";
              const ghostR = (NODE_RADIUS[ghostType] ?? NODE_RADIUS.body ?? 16) * 1.08;
              const ghostColors = NODE_COLORS[ghostType] || NODE_COLORS.body;
              const ghostIconSize = ghostR * 1.5;
              return (
                <>
                  <circle
                    r={ghostR + 5}
                    fill={ghostColors.sel || ghostColors.base}
                    opacity={0.22}
                  />
                  <circle
                    r={ghostR}
                    fill={ghostColors.sel || ghostColors.base}
                    stroke="#dbeafe"
                    strokeWidth={3}
                    strokeDasharray="5 4"
                  />
                  <foreignObject
                    x={-ghostIconSize / 2}
                    y={-ghostIconSize / 2}
                    width={ghostIconSize}
                    height={ghostIconSize}
                    style={{ pointerEvents: "none", opacity: 0.78 }}
                  >
                    <SceneNodeIcon type={ghostType} />
                  </foreignObject>
                </>
              );
            })()}
          </g>
        ) : null}
      </g>
    </>
  );
}
